<section class="alert-templates" aria-labelledby="alert-templates-title">
  <header>
    <div>
      <h2 id="alert-templates-title">Alert templates</h2>
      <p class="muted">Assignment alert wording · plain text</p>
    </div>
    @if (canRead() && !accessLost) {
      <button
        uiButton
        type="button"
        [disabled]="busy || loading || !catalog.canCreate"
        (click)="beginCreate()"
      >
        Create template
      </button>
    }
  </header>

  @if (!canRead() || accessLost) {
    <ui-standard-state
      kind="forbidden"
      message="Alert templates are unavailable with your current access."
      recoveryLabel="Check access"
      (recovery)="checkAccess()"
    />
  } @else {
    <form class="catalog-tools" (ngSubmit)="filter()">
      <label
        >Find a template
        <input
          uiControl
          name="template-search"
          [(ngModel)]="search"
          [disabled]="busy"
          type="search"
          maxlength="120"
        />
      </label>
      <button uiButton type="submit" [disabled]="busy || loading">
        Search
      </button>
      <button
        uiButton
        type="button"
        [disabled]="busy || loading"
        (click)="loadCatalog()"
      >
        Refresh list
      </button>
    </form>
    @if (loading) {
      <ui-standard-state kind="loading" message="Loading alert templates…" />
    } @else if (catalogError) {
      <ui-standard-state
        kind="error"
        [message]="catalogError"
        recoveryLabel="Retry list"
        (recovery)="loadCatalog()"
      />
    } @else if (!catalog.items.length) {
      <ui-standard-state
        kind="empty"
        [message]="
          search.trim()
            ? 'No alert templates match this search.'
            : 'No alert templates have been created in your authorized scopes.'
        "
      />
    } @else {
      <ui-grid
        [rowData]="catalog.items"
        [columnDefs]="columns"
        [agGridOptions]="gridOptions"
        [showPanelHeader]="false"
        [showBlankRowsOnLastPage]="false"
        [autoHidePaginator]="true"
      />
      <nav class="pager" aria-label="Alert template pages">
        <button
          uiButton
          type="button"
          [disabled]="busy || page <= 1"
          (click)="changePage(-1)"
        >
          Previous page
        </button>
        <span>Page {{ page }} · {{ catalog.totalCount }} templates</span>
        <button
          uiButton
          type="button"
          [disabled]="busy || page * catalog.pageSize >= catalog.totalCount"
          (click)="changePage(1)"
        >
          Next page
        </button>
      </nav>
    }

    @if (error) {
      <ui-standard-state kind="error" [message]="error" />
      @if (!detail && selectedDetailId !== null) {
        <button
          uiButton
          type="button"
          [disabled]="busy || detailLoading"
          (click)="retryDetail()"
        >
          Retry selected template
        </button>
      }
    }
    @if (notice) {
      <p role="status">{{ notice }}</p>
    }
    @if (busy) {
      <ui-standard-state
        kind="loading"
        message="Confirming template operation…"
      />
    }
    @if (detailLoading) {
      <ui-standard-state kind="loading" message="Loading selected template…" />
    }

    @if (showCreate) {
      <form
        class="editor"
        (ngSubmit)="create()"
        aria-labelledby="new-template-title"
      >
        <h3 id="new-template-title">Create assignment alert template</h3>
        <div class="fields">
          <label
            >Name<input
              uiControl
              name="template-name"
              [(ngModel)]="newName"
              (ngModelChange)="createChanged()"
              maxlength="160"
              [disabled]="busy"
              required
          /></label>
          <label
            >Stable code<input
              uiControl
              name="template-code"
              [(ngModel)]="newCode"
              (ngModelChange)="createChanged()"
              maxlength="120"
              [disabled]="busy"
              required
          /></label>
          <label
            >Scope
            <select
              uiControl
              name="template-scope"
              [(ngModel)]="createScope"
              (ngModelChange)="createChanged()"
              [disabled]="busy"
              required
            >
              <option value="">Choose an authorized scope</option>
              @for (scope of catalog.createScopes; track $index) {
                <option [value]="scopeIdentity(scope)">
                  {{ scope.label }}
                </option>
              }
            </select>
          </label>
        </div>
        <p class="muted">
          Creates a saved draft with initial wording. Preview, independent
          review, publication and active selection are separate actions.
        </p>
        <div class="actions">
          <button
            uiButton
            type="submit"
            [disabled]="
              busy ||
              !catalog.canCreate ||
              !newName.trim() ||
              !newCode.trim() ||
              createScope === ''
            "
          >
            Create draft
          </button>
          <button uiButton type="button" [disabled]="busy" (click)="discard()">
            Cancel
          </button>
        </div>
      </form>
    }

    @if (detail; as template) {
      <section class="editor" aria-labelledby="selected-template-title">
        <header>
          <div>
            <h3 id="selected-template-title">{{ template.name }}</h3>
            <p class="muted">
              {{ template.code }} · {{ template.scopeTypeCode
              }}{{ template.scopeKey ? " · " + template.scopeKey : "" }}
            </p>
          </div>
          <span class="source-state" role="status">{{
            dirty
              ? "Unsaved local changes"
              : "Saved draft revision " + template.draft.revision
          }}</span>
        </header>
        <form (ngSubmit)="save()" class="draft-form">
          <label>
            Data context
            <select
              uiControl
              name="alert-context"
              [(ngModel)]="draftProfileKey"
              (ngModelChange)="profileChanged()"
              [disabled]="!canEdit || profilesLoading"
              aria-describedby="alert-context-guidance"
            >
              @if (draftProfileKey && !selectedProfile) {
                <option [value]="draftProfileKey" disabled>
                  Saved context unavailable · refresh the catalog
                </option>
              }
              @for (profile of contextProfiles; track profileKey(profile)) {
                <option [value]="profileKey(profile)">
                  {{ profile.label }} · version {{ profile.version }}
                </option>
              }
            </select>
          </label>
          <p id="alert-context-guidance" class="muted">
            {{ selectedProfile?.description }}
            Context changes are saved with the draft. Published versions keep
            their original context.
          </p>
          @if (profilesLoading) {
            <ui-standard-state
              kind="loading"
              message="Loading data contexts…"
            />
          }
          @if (profilesError) {
            <ui-standard-state kind="error" [message]="profilesError" />
            <button
              uiButton
              type="button"
              [disabled]="busy"
              (click)="loadContextProfiles()"
            >
              Reload context catalog
            </button>
          }
          <label
            >Title<textarea
              #titleInput
              uiControl
              rows="2"
              name="alert-title"
              [(ngModel)]="titleTemplate"
              (ngModelChange)="inputChanged()"
              [disabled]="!canEdit"
              maxlength="201"
              aria-describedby="alert-text-guidance"
            ></textarea>
          </label>
          <label
            >Message body<textarea
              #bodyInput
              uiControl
              rows="6"
              name="alert-body"
              [(ngModel)]="bodyTemplate"
              (ngModelChange)="inputChanged()"
              [disabled]="!canEdit"
              maxlength="8001"
              aria-describedby="alert-text-guidance"
            ></textarea>
          </label>
          <p id="alert-text-guidance" class="muted">
            Title: up to 200 characters. Message: up to 8,000 characters.
          </p>
          @if (businessFields.length) {
            <div class="fields">
              <label>
                Business data field
                <select
                  uiControl
                  name="alert-data-field"
                  [(ngModel)]="selectedFieldCode"
                  [disabled]="!canEdit"
                >
                  <option value="">Choose a field</option>
                  @for (field of businessFields; track field.code) {
                    <option [value]="field.code">
                      {{ field.label }} · {{ field.dataType
                      }}{{ field.required ? " · required" : " · optional" }}
                    </option>
                  }
                </select>
              </label>
              <button
                uiButton
                type="button"
                [disabled]="!canEdit || !selectedFieldCode"
                (click)="insertField('title', titleInput)"
              >
                Insert in title
              </button>
              <button
                uiButton
                type="button"
                [disabled]="!canEdit || !selectedFieldCode"
                (click)="insertField('body', bodyInput)"
              >
                Insert in message
              </button>
            </div>
            <details class="context-fields">
              <summary>
                Available business data · {{ businessFields.length }} fields
              </summary>
              <p class="muted">
                These fields resolve from an authorized assignment source.
                Terminology references are unavailable in this context.
              </p>
              <dl>
                @for (field of businessFields; track field.code) {
                  <dt>{{ field.label }}</dt>
                  <dd>
                    <code>{{ "{{" + field.code + "}}" }}</code> ·
                    {{ field.dataType }} ·
                    {{ field.required ? "required" : "optional" }} ·
                    {{ field.classification }}<br />{{ field.maskingPolicy }}
                  </dd>
                }
              </dl>
            </details>
          } @else if (selectedProfile) {
            <p class="muted">
              This context accepts plain text without business data fields.
            </p>
          }
          @if (dirty && validationError) {
            <p role="alert">{{ validationError }}</p>
          }
          <div class="actions">
            @if (template.permissions.canAuthor) {
              <button uiButton type="submit" [disabled]="!canSave">
                Save draft
              </button>
            }
            <button
              uiButton
              type="button"
              [disabled]="busy || (!dirty && !reason.trim())"
              (click)="discard()"
            >
              Discard local changes
            </button>
            <button
              uiButton
              type="button"
              [disabled]="busy || detailLoading"
              (click)="refreshSource()"
            >
              Refresh source
            </button>
          </div>
        </form>
        <details>
          <summary>Saved source</summary>
          <dl>
            <dt>Saved revision</dt>
            <dd>{{ template.draft.revision }}</dd>
            <dt>Content hash</dt>
            <dd class="hash">{{ template.draft.contentHash }}</dd>
            <dt>Context version</dt>
            <dd>
              {{ template.draft.profileCode }} ·
              {{ template.draft.profileVersion }}
            </dd>
            <dt>Last author</dt>
            <dd>User {{ template.draft.lastAuthorUserId }}</dd>
            <dt>Saved</dt>
            <dd>{{ template.draft.savedUtc | date: "medium" }}</dd>
          </dl>
        </details>
        <div class="actions">
          <button
            uiButton
            type="button"
            [disabled]="!canPreview"
            (click)="preview()"
          >
            Preview saved revision
          </button>
          @if (template.permissions.canReview) {
            <button
              uiButton
              type="button"
              [disabled]="!canReview"
              (click)="review()"
            >
              Record independent review
            </button>
          }
          @if (template.permissions.canPublish) {
            <button
              uiButton
              type="button"
              [disabled]="!canPublish"
              (click)="publish()"
            >
              Publish reviewed revision
            </button>
          }
        </div>
        @if (previewBusy) {
          <ui-standard-state
            kind="loading"
            message="Preparing saved revision preview…"
          />
        }
        @if (draftPreview; as preview) {
          <section class="preview" aria-label="Saved revision preview">
            <h4>Saved revision {{ preview.draftRevision }} preview</h4>
            <strong>{{ preview.title }}</strong>
            <p class="message">{{ preview.body }}</p>
            <p class="muted">
              {{
                preview.contextEvidence
                  ? "Synthetic example · no live hearing or assignment was read"
                  : "Preview only · no recipients or delivery"
              }}
            </p>
            @if (preview.contextEvidence; as context) {
              <p class="muted">Preview only · no recipients or delivery</p>
              <details>
                <summary>Synthetic context values</summary>
                <p class="muted">{{ context.provenance }}</p>
                <dl>
                  @for (entry of contextEntries(preview); track entry[0]) {
                    <dt>
                      <code>{{ entry[0] }}</code>
                    </dt>
                    <dd>{{ entry[1] }}</dd>
                  }
                </dl>
              </details>
            }
          </section>
        }
        @if (currentReview; as review) {
          <p role="status">
            Revision {{ review.draftRevision }} independently reviewed by user
            {{ review.reviewerUserId }} on
            {{ review.reviewedUtc | date: "medium" }}.
          </p>
        } @else {
          <p class="muted">
            This saved revision needs independent review before publication. Its
            last author cannot record that review.
          </p>
        }
      </section>

      <section class="editor" aria-labelledby="published-template-title">
        <header>
          <h3 id="published-template-title">Published versions</h3>
          <span role="status">{{
            activeVersion
              ? "Active: version " + activeVersion.versionNumber
              : "No active version selected"
          }}</span>
        </header>
        @if (!template.versions.length) {
          <ui-standard-state
            kind="empty"
            message="No immutable version has been published. Saving a draft does not publish or select it."
          />
        } @else {
          <div class="fields">
            <label
              >Published version<select
                uiControl
                name="published-version"
                [(ngModel)]="selectedVersionId"
                (ngModelChange)="chooseVersion()"
                [disabled]="busy"
              >
                <option value="">
                  No version · clear selection for rollback
                </option>
                @for (version of template.versions; track version.id) {
                  <option [value]="version.id">
                    Version {{ version.versionNumber
                    }}{{ version.isRetired ? " · retired" : ""
                    }}{{
                      version.id === template.activeVersionId ? " · active" : ""
                    }}
                  </option>
                }
              </select></label
            >
            <button
              uiButton
              type="button"
              [disabled]="busy || !selectedVersion || versionPreviewBusy"
              (click)="previewVersion()"
            >
              Preview exact version
            </button>
          </div>
          @if (selectedVersion; as version) {
            <details>
              <summary>Version {{ version.versionNumber }} source</summary>
              <dl>
                <dt>Published</dt>
                <dd>{{ version.publishedUtc | date: "medium" }}</dd>
                <dt>Source draft revision</dt>
                <dd>{{ version.draftRevision }}</dd>
                <dt>Content hash</dt>
                <dd class="hash">{{ version.contentHash }}</dd>
                <dt>Context version</dt>
                <dd>
                  {{ version.profileCode }} · {{ version.profileVersion }}
                </dd>
                <dt>Reviewer</dt>
                <dd>User {{ version.reviewerUserId }}</dd>
                <dt>Publisher</dt>
                <dd>User {{ version.publisherUserId }}</dd>
              </dl>
            </details>
          }
          @if (versionPreviewBusy) {
            <ui-standard-state
              kind="loading"
              message="Loading retained version preview…"
            />
          }
          @if (versionPreview; as preview) {
            <section class="preview" aria-label="Retained version preview">
              <h4>Exact published version preview</h4>
              <strong>{{ preview.title }}</strong>
              <p class="message">{{ preview.body }}</p>
              <p class="muted">
                {{
                  preview.contextEvidence
                    ? "Retained template · synthetic context example · no live hearing or assignment was read"
                    : "Retained content · no recipients or delivery"
                }}
              </p>
            </section>
          }
          @if (
            template.permissions.canActivate ||
            template.permissions.canRetire ||
            template.permissions.canRollback
          ) {
            <label
              >Lifecycle reason<input
                uiControl
                name="lifecycle-reason"
                [(ngModel)]="reason"
                (ngModelChange)="reasonChanged()"
                maxlength="500"
                [disabled]="busy"
            /></label>
            <div class="actions">
              @if (template.permissions.canActivate) {
                <button
                  uiButton
                  type="button"
                  [disabled]="!canActivate"
                  (click)="select('activate')"
                >
                  Activate selected version
                </button>
              }
              @if (template.permissions.canRetire) {
                <button
                  uiButton
                  type="button"
                  [disabled]="!canRetire"
                  (click)="select('retire')"
                >
                  Retire selected version
                </button>
              }
              @if (template.permissions.canRollback) {
                <button
                  uiButton
                  type="button"
                  [disabled]="!canRollback"
                  (click)="select('rollback')"
                >
                  Roll back selection
                </button>
              }
            </div>
          }
        }
        <p class="muted">
          Selection revision {{ template.selectionRevision }}. Lifecycle changes
          preserve published content and previously prepared output.
        </p>
      </section>

      <details class="editor history">
        <summary>
          Template history · {{ template.history.length }} entries
        </summary>
        <ol>
          @for (entry of template.history; track entry.id) {
            <li>
              <strong>{{ entry.action }}</strong> ·
              {{ entry.occurredUtc | date: "medium" }} · user
              {{ entry.actorUserId }} · draft {{ entry.draftRevision }} ·
              selection {{ entry.selectionRevision }}
              @if (entry.reason) {
                <p>{{ entry.reason }}</p>
              }
            </li>
          }
        </ol>
      </details>
    }
  }
</section>
